import { useState } from 'react'
import { MessageSquarePlus } from 'lucide-react'
import type { FeedbackApi } from './feedbackApi.js'
import { FeedbackDrawer, type ScreenDetails } from './FeedbackDrawer.js'
import { CountBadge } from '../../core/index.js'

interface FeedbackButtonProps {
  api: FeedbackApi
  /** What the shell knows about the open screen - a facility, a worker, a dialog - for the screen context. */
  details: ScreenDetails
  /** Unread feedback to flag on the icon, for the admin; nothing is drawn for zero. */
  badge?: number
  /** Sits one step up from the corner, where the admin shell's API-calls icon is; elsewhere it takes the corner itself. */
  aboveInspector?: boolean
}

/**
 * A small fixed icon in the bottom-right corner of every signed-in screen
 * (above the admin shell's API-calls icon) that opens the feedback form. Discreet on purpose: it is there when
 * someone has something to say, not asking for attention.
 */
export function FeedbackButton({ api, details, badge = 0, aboveInspector = false }: FeedbackButtonProps) {
  const [isOpen, setIsOpen] = useState(false)

  return (
    <>
      <button
        type="button"
        onClick={() => setIsOpen((open) => !open)}
        aria-label={badge > 0 ? `Send product feedback (${badge} new)` : 'Send product feedback'}
        aria-expanded={isOpen}
        title="Send product feedback"
        className={`fixed ${aboveInspector ? 'bottom-16' : 'bottom-4'} right-4 z-40 flex h-9 w-9 items-center justify-center rounded-full shadow-lg transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-coral ${
          isOpen ? 'bg-coral text-white' : 'bg-white text-marine-dk border border-marine/20 hover:bg-marine-lt/60'
        }`}
      >
        <MessageSquarePlus aria-hidden="true" strokeWidth={1.75} className="h-4 w-4" />
        {badge > 0 && <CountBadge count={badge} />}
      </button>
      {isOpen && <FeedbackDrawer api={api} details={details} aboveInspector={aboveInspector} onClose={() => setIsOpen(false)} />}
    </>
  )
}
